// The title-block editor on a sheet: its mode, the title objects' selection,
// drags, clipboard and own undo, their panel, the presets to drag in, the inline
// text editor, and the save to the catalogue. docs/claude/sheets.md.
import {
  type InlineEditor,
  inlineEditor,
} from './inlineText';
import {
  defaultTitleObjects,
  TITLE_CATALOG,
  titleObjectPreset,
} from '../../shared/cartiglio';
import {
  type Cartiglio,
  type CartiglioSource,
  type Sheet,
  type TextI18n,
  type TitleObject,
  type Vec2,
} from '../../shared/types';
import {
  flashStatus,
  invalidate,
  newId,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import { GRIP_HIT_PX } from '../grip';
import { tr } from '../i18n';
import { platform } from '../platform';
import {
  type HandleId,
  paperToScreen,
  type PaperView,
  rectHandles,
  resizeRect,
  screenToPaper,
} from '../sheets';
import { axisSnapOffset } from '../snap';
import {
  $,
  canEditStandards,
  cartiglioForSheet,
  renderProductList,
  screenPos,
  SNAP_PX,
  undoSteps,
} from '../main';
import {
  activeSheet,
  paperView,
} from './sheetSpace';
import {
  flashClipboardElsewhere,
  takeClipboard,
} from './clipboard';
import {
  coreMenuItems,
  openContextMenu,
} from './contextMenus';
import {
  colorRow,
  deleteRow,
  numberRow,
  parseMeasure,
  sectionTitle,
} from './panelRows';
import {
  CART_SOURCES,
  cartigli,
} from './standards';
import {
  baseOfI18n,
  curLang,
  fillTranslations,
  i18nInputs,
  i18nText,
  seedI18n,
} from './nameFields';

/** This module's state that main.ts reads and writes too. */
export const titleEditor = {
  titleObjClipboard: null as TitleObject[] | null,
  titleObjClipboardSourceCartId: null as string | null,
  titleObjPasteCount: 0,
  selectedTitleObjIds: [] as string[],
  selectedTitleObjId: null as string | null,
  // Title-block direct editing: drag the cartiglio's objects on the sheet, then
  // persist to Baserow when the mode is turned off / the sheet left.
  titleEdit: false,
  titleObjDrag: null as | { mode: 'move'; startScreen: Vec2; objs: Array<{ id: string; x: number; y: number }>; moved: boolean }
  | { mode: 'resize'; handle: HandleId; id: string; startScreen: Vec2; obj: TitleObject; moved: boolean }
  | null,
  // Undo/redo scoped to the on-sheet title-block editor (cartigli live outside the
  // drawing's own history), plus the live dimension readout shown while dragging.
  titleReadout: null as { text: string; at: Vec2 } | null,
  titleObjDims: [] as TitleSideDim[],
  titleEditDirty: false,
};

/** The left panel while editing a title block: draggable title-object presets. */
export function renderTitleCatalog(list: HTMLElement): void {
  const head = document.createElement('div');
  head.className = 'hint';
  head.style.padding = '8px 10px';
  head.textContent = tr('Drag a block onto the sheet to add it.');
  list.appendChild(head);
  for (const item of TITLE_CATALOG) {
    const card = document.createElement('div');
    card.className = 'product-card';
    card.draggable = true;
    const swatch = document.createElement('div');
    swatch.className = 'swatch';
    swatch.style.background = item.kind === 'logo' ? '#6e1414' : '#2f2a28';
    const info = document.createElement('div');
    info.className = 'product-info';
    const name = document.createElement('div');
    name.className = 'product-name';
    name.textContent = tr(item.label);
    const kind = document.createElement('div');
    kind.className = 'product-dims';
    kind.textContent = tr('title block');
    info.append(name, kind);
    card.append(swatch, info);
    card.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('text/stenal-titleobj', item.kind);
      e.dataTransfer!.effectAllowed = 'copy';
    });
    list.appendChild(card);
  }
}

/** Add a new title object from the catalogue at a dropped screen point. */
export function dropTitleObject(kind: string, pos: Vec2): void {
  const sh = activeSheet(); if (!sh || !titleEditor.titleEdit) return;
  const cart = cartiglioForSheet(sh); if (!cart || !cart.objects) return;
  const p = screenToPaper(paperView(), pos);
  pushTitleObjHistory();
  const o = titleObjectPreset(kind, Math.max(0, p.x - 15), Math.max(0, p.y - 8));
  o.id = `${kind}-${(cart.objects.length + 1)}-${Math.round(p.x)}x${Math.round(p.y)}`;
  cart.objects.push(o);
  selectTitleObjOnly(o.id);
  titleEditor.titleEditDirty = true; redrawBackdrop(); renderProperties(); invalidate();
}

// ---- Object-model title-block editor (free-form title objects) ----
const TB_INNER_MARGIN = 5; // imaginary 5 mm inner margin objects snap to
const TB_PASTE_OFFSET = 8; // mm shift per paste, only when pasting back onto the same cartiglio

export function selectedTitleObj(cart: Cartiglio): TitleObject | null {
  return (cart.objects || []).find((o) => o.id === titleEditor.selectedTitleObjId) ?? null;
}
/** Every currently selected title object (the multi-selection), in cart order. */
export function selectedTitleObjs(cart: Cartiglio): TitleObject[] {
  return (cart.objects || []).filter((o) => titleEditor.selectedTitleObjIds.includes(o.id));
}
/** Replace the selection with a single object (or clear it), resetting multi-select. */
export function selectTitleObjOnly(id: string | null): void {
  titleEditor.selectedTitleObjId = id;
  titleEditor.selectedTitleObjIds = id ? [id] : [];
}
/** Toggle one object in/out of the multi-selection (shift-click). */
export function toggleTitleObjSelection(id: string): void {
  if (titleEditor.selectedTitleObjIds.includes(id)) {
    titleEditor.selectedTitleObjIds = titleEditor.selectedTitleObjIds.filter((x) => x !== id);
    titleEditor.selectedTitleObjId = titleEditor.selectedTitleObjIds[titleEditor.selectedTitleObjIds.length - 1] ?? null;
  } else {
    titleEditor.selectedTitleObjIds = [...titleEditor.selectedTitleObjIds, id];
    titleEditor.selectedTitleObjId = id;
  }
}
/** Topmost title object containing paper-mm point `p` (last drawn = on top). */
export function titleObjAt(cart: Cartiglio, p: Vec2): TitleObject | null {
  const objs = cart.objects || [];
  for (let i = objs.length - 1; i >= 0; i--) {
    const o = objs[i];
    if (p.x >= o.x && p.x <= o.x + o.w && p.y >= o.y && p.y <= o.y + o.h) return o;
  }
  return null;
}
/** The 8 resize handles (screen space) of a title object's rect. */
export function titleObjHandles(pv: PaperView, o: TitleObject): Array<{ id: HandleId; x: number; y: number }> {
  const a = paperToScreen(pv, o.x, o.y);
  return rectHandles({ x: a.x, y: a.y, w: o.w * pv.scale, h: o.h * pv.scale });
}
export function hitTitleObjHandle(pv: PaperView, o: TitleObject, pos: Vec2): HandleId | null {
  for (const h of titleObjHandles(pv, o)) if (Math.abs(pos.x - h.x) <= GRIP_HIT_PX && Math.abs(pos.y - h.y) <= GRIP_HIT_PX) return h.id;
  return null;
}
/** Snap a moving object's proposed top-left (paper mm) to siblings, the page
 *  border and the 5 mm inner margin, then to the nearest 1 mm grid point.
 *  `excludeIds` skips extra objects (the rest of a multi-selection being
 *  dragged together) as snap candidates, on top of `o` itself. */
export function snapTitleObjMove(sh: Sheet, cart: Cartiglio, o: TitleObject, nx: number, ny: number, excludeIds?: Set<string>): Vec2 {
  const tol = SNAP_PX / paperView().scale;
  const probes: Vec2[] = [
    { x: nx, y: ny }, { x: nx + o.w / 2, y: ny + o.h / 2 }, { x: nx + o.w, y: ny + o.h },
    { x: nx + o.w, y: ny }, { x: nx, y: ny + o.h },
  ];
  const cx: number[] = [0, TB_INNER_MARGIN, sh.widthMm - TB_INNER_MARGIN, sh.widthMm];
  const cy: number[] = [0, TB_INNER_MARGIN, sh.heightMm - TB_INNER_MARGIN, sh.heightMm];
  for (const s of cart.objects || []) {
    if (s.id === o.id || excludeIds?.has(s.id)) continue;
    cx.push(s.x, s.x + s.w / 2, s.x + s.w);
    cy.push(s.y, s.y + s.h / 2, s.y + s.h);
  }
  const candidates: Vec2[] = [];
  for (const x of cx) for (const y of cy) candidates.push({ x, y });
  const snap = axisSnapOffset(probes, candidates, tol);
  return { x: Math.round(nx + snap.offset.x), y: Math.round(ny + snap.offset.y) };
}
/** Apply a resize-handle drag to a title object (paper-mm cursor point), snapped
 *  to the nearest 1 mm grid point on every edge the handle moves. */
/** A title-block object dragged by a handle: at least 6 mm, its edges on whole mm. */
export function resizeTitleObj(o: TitleObject, start: TitleObject, handle: HandleId, p: Vec2): void {
  const r = resizeRect(start, handle, p, 6);
  const x0 = Math.round(r.x), y0 = Math.round(r.y), x1 = Math.round(r.x + r.w), y1 = Math.round(r.y + r.h);
  o.x = x0; o.y = y0; o.w = x1 - x0; o.h = y1 - y0;
}
interface TitleSideDim { side: 'l' | 'r' | 't' | 'b'; from: Vec2; to: Vec2; mm: number; }
/** For each side of `o`, the distance (mm) to the nearest thing beyond it —
 *  another title object's facing edge (only counted when it overlaps `o` on
 *  the perpendicular axis) or, failing that, the sheet edge. One entry per
 *  side with a positive gap, for the live dimension lines shown while
 *  dragging/resizing a title object. */
export function titleObjSideDims(sh: Sheet, cart: Cartiglio, o: TitleObject): TitleSideDim[] {
  const others = (cart.objects || []).filter((s) => s.id !== o.id);
  const midY = o.y + o.h / 2, midX = o.x + o.w / 2;
  const EPS = 0.5;
  const out: TitleSideDim[] = [];
  let leftX = 0;
  for (const s of others) { if (s.y + s.h <= o.y || s.y >= o.y + o.h) continue; const e = s.x + s.w; if (e <= o.x + EPS && e > leftX) leftX = e; }
  if (o.x - leftX > EPS) out.push({ side: 'l', from: { x: leftX, y: midY }, to: { x: o.x, y: midY }, mm: o.x - leftX });
  let rightX = sh.widthMm;
  for (const s of others) { if (s.y + s.h <= o.y || s.y >= o.y + o.h) continue; if (s.x >= o.x + o.w - EPS && s.x < rightX) rightX = s.x; }
  if (rightX - (o.x + o.w) > EPS) out.push({ side: 'r', from: { x: o.x + o.w, y: midY }, to: { x: rightX, y: midY }, mm: rightX - (o.x + o.w) });
  let topY = 0;
  for (const s of others) { if (s.x + s.w <= o.x || s.x >= o.x + o.w) continue; const e = s.y + s.h; if (e <= o.y + EPS && e > topY) topY = e; }
  if (o.y - topY > EPS) out.push({ side: 't', from: { x: midX, y: topY }, to: { x: midX, y: o.y }, mm: o.y - topY });
  let botY = sh.heightMm;
  for (const s of others) { if (s.x + s.w <= o.x || s.x >= o.x + o.w) continue; if (s.y >= o.y + o.h - EPS && s.y < botY) botY = s.y; }
  if (botY - (o.y + o.h) > EPS) out.push({ side: 'b', from: { x: midX, y: o.y + o.h }, to: { x: midX, y: botY }, mm: botY - (o.y + o.h) });
  return out;
}
export function pushTitleObjHistory(): void { pushTitleHistory(); }
/** Delete every selected title object (the whole multi-selection). */
export function deleteSelectedTitleObj(): void {
  const sh = activeSheet(); const cart = sh ? cartiglioForSheet(sh) : null;
  if (!cart || !cart.objects || !titleEditor.selectedTitleObjIds.length) return;
  pushTitleObjHistory();
  const ids = new Set(titleEditor.selectedTitleObjIds);
  cart.objects = cart.objects.filter((o) => !ids.has(o.id));
  selectTitleObjOnly(null);
  titleEditor.titleEditDirty = true; redrawBackdrop(); renderProperties(); invalidate();
}
/** Regenerate the default free-form layout for this sheet's paper+orientation. */
export function resetTitleObjects(sh: Sheet): void {
  const cart = cartiglioForSheet(sh); if (!cart || !cart.objects) return;
  pushTitleObjHistory();
  cart.objects = defaultTitleObjects(sh.paper, sh.widthMm, sh.heightMm);
  selectTitleObjOnly(null);
  titleEditor.titleEditDirty = true; redrawBackdrop(); renderProperties(); invalidate();
}
/** Copy every selected title object (one or more) to the title-block clipboard,
 *  remembering the source cartiglio so a same-sheet paste offsets visibly while
 *  a cross-sheet paste lands in the same position it was copied from. */
export function copyTitleObjSelection(): void {
  const sh = activeSheet(); if (!sh) return;
  const cart = cartiglioForSheet(sh); if (!cart || !cart.objects) return;
  const sel = selectedTitleObjs(cart);
  if (!sel.length) return;
  takeClipboard('title');
  titleEditor.titleObjClipboard = sel.map((o) => structuredClone(o));
  titleEditor.titleObjClipboardSourceCartId = cart.id;
  titleEditor.titleObjPasteCount = 0;
  flashStatus(sel.length > 1 ? tr('Copied {n} title blocks.').replace('{n}', String(sel.length)) : tr('Copied title block.'));
}
/** Paste the title-block clipboard into the active sheet's cartiglio (any sheet,
 *  including one with a different paper/orientation/company cartiglio — that's
 *  the point of copying "between sheets"), selecting the pasted copies. */
export function pasteTitleObjClipboard(): void {
  if (!titleEditor.titleObjClipboard || !titleEditor.titleObjClipboard.length) { flashClipboardElsewhere('title'); return; }
  const sh = activeSheet(); if (!sh) return;
  const cart = cartiglioForSheet(sh); if (!cart || !cart.objects) return;
  pushTitleObjHistory();
  const off = cart.id === titleEditor.titleObjClipboardSourceCartId ? TB_PASTE_OFFSET * (titleEditor.titleObjPasteCount + 1) : 0;
  const newIds: string[] = [];
  for (const src of titleEditor.titleObjClipboard) {
    const copy: TitleObject = structuredClone(src);
    copy.id = newId(copy.id.split('-')[0] || 'obj');
    copy.x = Math.round(copy.x + off);
    copy.y = Math.round(copy.y + off);
    cart.objects.push(copy);
    newIds.push(copy.id);
  }
  titleEditor.titleObjPasteCount++;
  titleEditor.selectedTitleObjIds = newIds;
  titleEditor.selectedTitleObjId = newIds[newIds.length - 1];
  titleEditor.titleEditDirty = true; redrawBackdrop(); renderProperties(); invalidate();
  flashStatus(newIds.length > 1 ? tr('Pasted {n} title blocks.').replace('{n}', String(newIds.length)) : tr('Pasted title block.'));
}
/** Mutate the selected object and repaint (marks the cartiglio dirty). */
function editTitleObj(mut: () => void, record = true): void {
  if (record) pushTitleObjHistory();
  mut();
  titleEditor.titleEditDirty = true; redrawBackdrop(); invalidate();
}
/** EN/IT/FR editor for a title object's translatable string. Keeps the base
 *  (source-language) copy in sync so a drawing whose language is missing still
 *  shows the text, and marks the cartiglio dirty so the edit is persisted. */
function titleI18nRow(label: string, i18n: TextI18n, syncBase: (base: string) => void, multiline: boolean): HTMLElement {
  const wrap = document.createElement('div'); wrap.className = 'prop-row'; wrap.style.flexDirection = 'column'; wrap.style.alignItems = 'stretch';
  const l = document.createElement('label'); l.textContent = tr(label); l.style.marginBottom = '4px';
  wrap.appendChild(l);
  wrap.appendChild(i18nInputs(i18n, { multiline, auto: true, onChange: () => { syncBase(baseOfI18n(i18n)); titleEditor.titleEditDirty = true; redrawBackdrop(); invalidate(); } }));
  return wrap;
}
/** Property panel for one selected title object. */
export function renderTitleObjectProps(body: HTMLElement, sh: Sheet, cart: Cartiglio, o: TitleObject): void {
  const roleLabel: Record<TitleObject['role'], string> = { logo: 'Logo', address: 'Address', field: 'Field', text: 'Text' };
  body.appendChild(sectionTitle(`${tr('Block')} — ${tr(roleLabel[o.role])}`));

  // Position & size.
  body.appendChild(numberRow('X (mm)', o.x, (v) => editTitleObj(() => { o.x = v; }), { commit: true }));
  body.appendChild(numberRow('Y (mm)', o.y, (v) => editTitleObj(() => { o.y = v; }), { commit: true }));
  body.appendChild(numberRow('Width (mm)', o.w, (v) => editTitleObj(() => { o.w = Math.max(6, v); }), { commit: true }));
  body.appendChild(numberRow('Height (mm)', o.h, (v) => editTitleObj(() => { o.h = Math.max(6, v); }), { commit: true }));

  // Borders (per side + colour).
  body.appendChild(sectionTitle('Borders (mm)'));
  const bRow = document.createElement('div'); bRow.className = 'prop-row';
  const bLab = document.createElement('label'); bLab.textContent = 'T · R · B · L';
  const wrap = document.createElement('div'); wrap.style.display = 'flex'; wrap.style.gap = '4px'; wrap.style.flex = '1';
  const sides: Array<keyof Pick<TitleObject['border'], 'top' | 'right' | 'bottom' | 'left'>> = ['top', 'right', 'bottom', 'left'];
  for (const side of sides) {
    const inp = document.createElement('input'); inp.type = 'text'; inp.inputMode = 'decimal';
    inp.value = String(o.border[side]); inp.style.width = '100%'; inp.style.textAlign = 'center';
    inp.addEventListener('input', () => { const v = parseMeasure(inp.value, 'mm'); if (v !== null) editTitleObj(() => { o.border[side] = Math.max(0, v); }); });
    wrap.appendChild(inp);
  }
  bRow.append(bLab, wrap); body.appendChild(bRow);
  body.appendChild(colorRow('Border colour', o.border.color, (hex, first) => editTitleObj(() => { o.border.color = hex; }, first), { ownStep: true }));

  // Title word (all roles except pure logo) — translated per drawing language.
  if (o.role !== 'logo') {
    body.appendChild(sectionTitle('Title'));
    o.wordI18n = seedI18n(o.word, o.wordI18n);
    body.appendChild(titleI18nRow('Word (EN · IT · FR)', o.wordI18n, (b) => { o.word = b; }, false));
    body.appendChild(numberRow('Text height (mm)', o.wordFontMm, (v) => editTitleObj(() => { o.wordFontMm = Math.max(0, v); }), { commit: true }));
    body.appendChild(colorRow('Colour', o.wordColor, (hex, first) => editTitleObj(() => { o.wordColor = hex; }, first), { ownStep: true }));
  }

  // Dynamic value (field source or literal text).
  if (o.role === 'field') {
    body.appendChild(sectionTitle('Value'));
    const srcRow = document.createElement('div'); srcRow.className = 'prop-row';
    const srcLab = document.createElement('label'); srcLab.textContent = tr('Source');
    const sel = document.createElement('select');
    for (const [v, lbl] of CART_SOURCES) { const opt = document.createElement('option'); opt.value = v; opt.textContent = tr(lbl); sel.appendChild(opt); }
    sel.value = o.source ?? 'blank';
    sel.addEventListener('change', () => editTitleObj(() => { o.source = sel.value as CartiglioSource; renderProperties(); }));
    srcRow.append(srcLab, sel); body.appendChild(srcRow);
    if ((o.source ?? 'blank') === 'literal') {
      o.literalI18n = seedI18n(o.literal ?? '', o.literalI18n);
      body.appendChild(titleI18nRow('Fixed text (EN · IT · FR)', o.literalI18n, (b) => { o.literal = b; }, true));
    }
    body.appendChild(numberRow('Text height (mm)', o.valueFontMm, (v) => editTitleObj(() => { o.valueFontMm = Math.max(0, v); }), { commit: true }));
    body.appendChild(colorRow('Colour', o.valueColor, (hex, first) => editTitleObj(() => { o.valueColor = hex; }, first), { ownStep: true }));
  } else if (o.role === 'address' || o.role === 'text') {
    body.appendChild(sectionTitle('Text'));
    o.literalI18n = seedI18n(o.literal ?? '', o.literalI18n);
    body.appendChild(titleI18nRow('Content (EN · IT · FR)', o.literalI18n, (b) => { o.literal = b; }, true));
    body.appendChild(numberRow('Text height (mm)', o.valueFontMm, (v) => editTitleObj(() => { o.valueFontMm = Math.max(0, v); }), { commit: true }));
    body.appendChild(colorRow('Colour', o.valueColor, (hex, first) => editTitleObj(() => { o.valueColor = hex; }, first), { ownStep: true }));
  }

  body.appendChild(deleteRow(() => deleteSelectedTitleObj()));
}

/** Snapshot the active sheet's cartiglio before a title-block edit, onto the ONE
 *  undo stack: a title block lives outside the drawing, so its entry is a foreign
 *  one (`swapTitleCart` steps it), shared before and after the editor closes. */
function pushTitleHistory(): void {
  const sh = activeSheet();
  const cart = sh ? cartiglioForSheet(sh) : null;
  if (!cart) return;
  undoSteps.pushForeign(JSON.stringify(cart));
}
/** Undo / redo reached a title-block entry: put that title block back, and hand
 *  over the one it replaces for the other stack (none: it no longer exists). */
export function swapTitleCart(json: string): string | null {
  const id = (JSON.parse(json) as Cartiglio).id;
  const cur = cartigli.find((c) => c.id === id);
  const was = cur ? JSON.stringify(cur) : null;
  restoreTitleCart(json);
  return was;
}
/** Put a title block back (undo / redo). Inside the editor it is saved when the
 *  editor closes; outside it, straight away — it is a shared standard. */
function restoreTitleCart(json: string): void {
  const c = JSON.parse(json) as Cartiglio;
  const i = cartigli.findIndex((x) => x.id === c.id);
  if (i >= 0) cartigli[i] = c;
  titleEditor.titleEditDirty = true; redrawBackdrop(); renderProperties(); invalidate();
  if (!titleEditor.titleEdit) void commitTitleEdit(c.id);
}
/** PowerPoint's "Slide Master" banner over the page while the title block is being
 *  edited: what is being edited, that it is shared, and the way out. */
export function renderMasterBar(): void {
  const bar = $('master-bar');
  const sh = activeSheet();
  const cart = sh ? cartiglioForSheet(sh) : null;
  bar.classList.toggle('hidden', !titleEditor.titleEdit || !cart);
  if (!titleEditor.titleEdit || !cart) return;
  bar.innerHTML = '';
  const text = document.createElement('span');
  text.textContent = `${tr('Title block master')}: ${cart.name} — ${tr('changes apply to every sheet that uses it.')}`;
  const close = document.createElement('button');
  close.className = 'primary mini';
  close.textContent = tr('Close master view');
  close.addEventListener('click', () => setTitleEdit(false));
  bar.append(text, close);
}

/** Persist the cartiglio being edited (once), refreshing the local catalogue. */
export async function commitTitleEdit(cartId?: string): Promise<void> {
  if (!canEditStandards() || !titleEditor.titleEditDirty) return;
  titleEditor.titleEditDirty = false;
  const sh = activeSheet();
  const cart = cartId ? cartigli.find((c) => c.id === cartId) ?? null : sh ? cartiglioForSheet(sh) : null;
  if (!cart) return;
  try {
    const saved = await platform.saveCartiglio(cart);
    const i = cartigli.findIndex((c) => c.id === saved.id);
    if (i >= 0) cartigli[i] = saved;
    flashStatus(tr('Title block saved.'));
  } catch (err) {
    flashStatus(tr('Could not save the title block') + ': ' + (err as Error).message);
  }
}
export function setTitleEdit(on: boolean): void {
  if (titleEditor.titleEdit === on) return;
  titleEditor.titleEdit = on;
  titleEditor.titleObjDrag = null;
  titleEditor.titleObjDims = [];
  selectTitleObjOnly(null);
  titleEditor.titleReadout = null;
  if (!on) void commitTitleEdit();
  renderMasterBar();
  renderProductList(); // swap the left panel to/from the title-object catalogue
  renderProperties();
  invalidate();
}

/** Right-click in the title-block editor: selects the object under the cursor
 *  (keeping a multi-selection it belongs to) and offers Copy / Paste / Delete. */
export function showTitleObjMenu(e: MouseEvent): void {
  const sh = activeSheet(); const cart = sh ? cartiglioForSheet(sh) : null;
  if (!cart) return;
  const o = titleObjAt(cart, screenToPaper(paperView(), screenPos(e)));
  if (o && !titleEditor.selectedTitleObjIds.includes(o.id)) { selectTitleObjOnly(o.id); renderProperties(); invalidate(); }
  openContextMenu(e, coreMenuItems('title'));
}

// Inline editing of a title object's text on the sheet (double-click a field
// value, the address, or a legal/text block). Meta-bound field values write to
// the drawing; literal / address / text bodies write to the cartiglio object.
const META_SOURCES = ['descrizione', 'commessa', 'versione', 'data', 'autore'] as const;
type MetaSource = typeof META_SOURCES[number];
let titleInline: InlineEditor | null = null;

/** Inline-edit an object's literal body in the CURRENT drawing language: writes
 *  that language's variant, keeps the base copy in sync, and auto-translates the
 *  still-empty languages. */
function titleLiteralTarget(o: TitleObject): { get: () => string; set: (v: string) => void; multiline: boolean } {
  return {
    multiline: true,
    get: () => i18nText(o.literal || '', o.literalI18n),
    set: (v) => {
      editTitleObj(() => {
        o.literalI18n = seedI18n(o.literal || '', o.literalI18n);
        o.literalI18n[curLang()] = v.trim();
        o.literal = baseOfI18n(o.literalI18n) || o.literal || '';
      });
      void fillTranslations(o.literalI18n!, curLang()).then(() => { redrawBackdrop(); invalidate(); });
    },
  };
}
/** The editable text of an object: meta field, literal, or block content. */
function titleObjTextTarget(o: TitleObject): { get: () => string; set: (v: string) => void; multiline: boolean } | null {
  if (o.role === 'field') {
    const src = o.source ?? 'blank';
    if ((META_SOURCES as readonly string[]).includes(src)) {
      return { multiline: false, get: () => (state.drawing.meta?.[src as MetaSource]) || '', set: (v) => { pushHistory(); (state.drawing.meta ||= {})[src as MetaSource] = v.trim(); } };
    }
    if (src === 'literal') return titleLiteralTarget(o);
    return null; // auto (foglio/scala) or blank — nothing to type
  }
  if (o.role === 'address' || o.role === 'text') return titleLiteralTarget(o);
  return null;
}
/** Edit a title-block object's text inline: a field of its own over the object
 *  (ui/inlineText.ts), Enter commits (Ctrl/Cmd+Enter in a multi-line one),
 *  Escape puts the text back, a click away commits. */
export function openTitleObjInlineEditor(o: TitleObject, rect: { x: number; y: number; w: number; h: number }): void {
  const tgt = titleObjTextTarget(o); if (!tgt) return;
  titleInline?.close(true);
  const el = tgt.multiline ? document.createElement('textarea') : document.createElement('input');
  Object.assign(el.style, { position: 'fixed', font: '12px Arial, sans-serif', zIndex: '9999', boxSizing: 'border-box', resize: 'none', minHeight: '0' });
  document.body.appendChild(el);
  const ed = inlineEditor(el, true);
  titleInline = ed;
  ed.open({
    text: tgt.get(),
    look: () => ({
      left: `${rect.x}px`, top: `${rect.y}px`, width: `${Math.max(60, rect.w)}px`, height: `${Math.max(tgt.multiline ? 34 : 16, rect.h)}px`,
      border: '1.5px solid #4aa3ff', padding: '1px 3px', background: '#fff', color: '#111',
    }),
    keys: { enter: tgt.multiline ? 'ctrl-commits' : 'commits', esc: 'cancels' },
    gesture: false,
    caret: tgt.multiline ? 'end' : 'all',
    commit: (v) => { tgt.set(v); redrawBackdrop(); renderProperties(); },
    closed: () => { if (titleInline === ed) titleInline = null; invalidate(); },
  });
}
